import { usePrevious } from '@partful/shared/lib/hooks/usePrevious';
import { useCallback, useEffect, useReducer } from 'react';
import { Material, Mesh, Object3D } from 'three';
import { Animation } from '../model/animation';
import { Channel } from '../model/channel';
import { Keyframe } from '../model/keyframe';

type ResetAction = {
  type: 'reset';
  animation: Animation;
};
type AddKeyframeAction = {
  type: 'add-keyframe';
  objects: Object3D[];
  time: number;
};
type RemoveKeyframeAction = {
  type: 'remove-keyframe';
  objects: Object3D[];
  time: number;
};
type ToggleObjectVisibileAction = {
  type: 'toggle-object-visible';
  objects: Object3D[];
};
type ToggleObjectHighlightAction = {
  type: 'toggle-object-highlight';
  objects: Object3D[];
};

type AnimationAction =
  | ResetAction
  | AddKeyframeAction
  | RemoveKeyframeAction
  | ToggleObjectVisibileAction
  | ToggleObjectHighlightAction;

function getChannel(animation: Animation, name: string, make: true): Channel;
function getChannel(animation: Animation, name: string, make?: boolean): Channel | undefined;
function getChannel(animation: Animation, name: string, make?: boolean): Channel | undefined {
  if (animation.channels[name] == null && make) {
    animation.channels[name] = {
      hidden: false,
      highlighted: false,
      keyframes: [],
    };
  }

  return animation.channels[name];
}

function isChannelRedundant(channel: Channel) {
  if (channel.hidden || channel.highlighted || channel.keyframes.length) {
    return false;
  }
  return true;
}

function cleanAnimation(animation: Animation) {
  const channelNames = Object.keys(animation.channels);

  channelNames.forEach((name) => {
    const channel = animation.channels[name];
    if (channel && isChannelRedundant(animation.channels[name])) {
      delete animation.channels[name];
    }
  });

  return animation;
}

function addKeyframeToAnimation(animation: Animation, object: Object3D, time: number) {
  const channel = getChannel(animation, object.name, true);

  const keyframe: Keyframe = {
    time,
    position: object.position.toArray(),
    rotation: object.quaternion.toArray(),
  };

  if (object instanceof Mesh && object.material instanceof Material) {
    keyframe.opacity = object.material.opacity;
  }

  const { keyframes } = channel;
  const index = keyframes.findIndex((it) => it.time === time);

  if (index !== -1) {
    keyframes[index] = keyframe;
  } else {
    keyframes.push(keyframe);
    keyframes.sort((a, b) => a.time - b.time);
  }
}

function removeKeyframeFromAnimation(animation: Animation, object: Object3D, time: number) {
  const channel = getChannel(animation, object.name);
  if (channel) {
    const { keyframes } = channel;
    const index = keyframes.findIndex((it) => it.time === time);

    if (index !== -1) {
      keyframes.splice(index, 1);
    }
  }
}

function toggleObjectVisibleInAnimation(animation: Animation, object: Object3D) {
  const { name } = object;
  const channel = getChannel(animation, name, true);
  channel.hidden = !channel.hidden;
  object.visible = !channel.hidden; // TODO - should this be handled by the model animator!?
}

function toggleObjectHighlightInAnimation(animation: Animation, object: Object3D) {
  const { name } = object;
  const channel = getChannel(animation, name, true);
  channel.highlighted = !channel.highlighted;
}

export const useAnimationBuilder = (
  initialAnimation: Animation = { channels: {} },
  onChange?: (animation: Animation) => void
) => {
  const animationReducer = useCallback(
    (animation: Animation, action: AnimationAction) => {
      if (action.type === 'reset') {
        return action.animation;
      }

      const newAnimation: Animation = JSON.parse(JSON.stringify(animation));

      if (action.type === 'add-keyframe') {
        const { objects, time } = action;
        objects.forEach((object) => {
          addKeyframeToAnimation(newAnimation, object, time);
        });
      }

      if (action.type === 'remove-keyframe') {
        const { objects, time } = action;
        objects.forEach((object) => {
          removeKeyframeFromAnimation(newAnimation, object, time);
        });
      }

      if (action.type === 'toggle-object-visible') {
        const { objects } = action;
        objects.forEach((object) => {
          toggleObjectVisibleInAnimation(newAnimation, object);
        });
      }

      if (action.type === 'toggle-object-highlight') {
        const { objects } = action;
        objects.forEach((object) => {
          toggleObjectHighlightInAnimation(newAnimation, object);
        });
      }

      cleanAnimation(newAnimation);
      onChange?.(newAnimation);
      return newAnimation;
    },
    [onChange]
  );

  const [animation, dispatch] = useReducer(animationReducer, initialAnimation);
  const previous = usePrevious(initialAnimation);

  // If the initialAnimation changes, start afresh.
  useEffect(() => {
    if (JSON.stringify(previous) !== JSON.stringify(initialAnimation)) {
      dispatch({ type: 'reset', animation: initialAnimation });
    }
  }, [initialAnimation, previous]);

  const addKeyframe = useCallback((time: number, objects: Object3D[]) => {
    dispatch({ type: 'add-keyframe', objects, time });
  }, []);

  const removeKeyframe = useCallback((time: number, objects: Object3D[]) => {
    dispatch({ type: 'remove-keyframe', objects, time });
  }, []);

  const toggleObjectVisible = useCallback((objects: Object3D[]) => {
    dispatch({ type: 'toggle-object-visible', objects });
  }, []);

  const toggleObjectHighlight = useCallback((objects: Object3D[]) => {
    dispatch({ type: 'toggle-object-highlight', objects });
  }, []);

  return {
    animation,
    addKeyframe,
    removeKeyframe,
    toggleObjectVisible,
    toggleObjectHighlight,
  } as const;
};
